import "twin.macro";

import { useState } from "react";
import { useSelector } from "react-redux";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import _ from "lodash";

import { ItemPreviewImageButton, variantName } from "@features/items";
import { CreateInventoryPOVModal } from "@features/purchaseOrders";
import { usePurchaseOrder } from "@features/purchaseOrders/context/PurchaseOrderContext";
import { formatMoneyString } from "@utility/utilityFunctions";
import permissions from "@utils/permissions";
import useRoleIs from "@utils/useRoleIs";

import StandardTableHead from "../Utility/StandardTableHead";
import PurchaseOrderVariantTableFooter from "./PurchaseOrderVariantTableFooter";

const useStyles = makeStyles((theme: any) => ({
  ...theme.global,
}));

const PurchaseOrderVariantOverviewTable = ({ setTableView }) => {
  const classes: any = useStyles();
  const roleIs = useRoleIs();
  const { purchaseOrderVariants } = usePurchaseOrder();
  const {
    organization: { usesItemCustomization },
  } = useSelector((state: any) => state.currentUser);

  const povItems = _(purchaseOrderVariants)
    .filter((pov) => !pov.isSetupFee)
    .sortBy("sku", "isToInventory")
    .value();

  const setupFees = _(purchaseOrderVariants)
    .filter((pov) => pov.isSetupFee)
    .sortBy("setupFeeDesc")
    .value();

  const povBySku = _.groupBy(povItems, "variant.variantSku");

  const [inventoryModalOpen, setInventoryModalOpen] = useState(null);
  const disabledInventorySkus = _(povItems)
    .filter((pov) => pov.isToInventory)
    .map((pov) => pov.variant.variantSku)
    .value();

  let headCells = [
    { id: "image", label: "Preview" },
    { id: "sku", label: "Sku" },
    { id: "name", label: "Name" },
    { id: "variant", label: "Variant" },
    { id: "customization", label: "Customization" },
    { id: "packSize", label: "Pack Size", align: "right" },
    { id: "qty", label: "Total Qty", align: "right" },
    { id: "cost", label: "Est. Cost", align: "right" },
    { id: "actCost", label: "Act. Cost", align: "right" },
    { id: "total", label: "Total", align: "right" },
  ];

  if (roleIs(permissions.supplier)) {
    headCells = headCells.filter(
      (cell) => cell.id !== "actions" && cell.id !== "cost"
    );
  }

  if (!usesItemCustomization) {
    headCells = headCells.filter((cell) => cell.id !== "customization");
  }

  const totalRowsFirstCellLength = headCells.length - 2;

  return (
    <>
      {inventoryModalOpen && (
        <CreateInventoryPOVModal
          itemId={povItems[0].variant.item.id}
          handleClose={() => setInventoryModalOpen(null)}
          disabledSkus={disabledInventorySkus}
        />
      )}
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <StandardTableHead headCells={headCells} />
          <TableBody>
            {Object.values(povBySku).map((povs) => {
              const pov = povs[0];
              const extendedTotal = _.sumBy(
                povs,
                (pov) => +pov.actualCost * pov.qty
              );
              const totalQty = _.sumBy(povs, (pov) => +pov.qty);
              const hasDifferentCosts = povs.some(
                (pov) => pov.actualCost !== povs[0].actualCost
              );

              return (
                <TableRow key={pov.id}>
                  <TableCell>
                    <ItemPreviewImageButton
                      partialItem={pov.variant.item}
                      imageId={pov.variant.imageId}
                      tw="w-14"
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      {pov.variant.externalWarehouseId ??
                        pov.variant.variantSku}
                    </div>
                  </TableCell>
                  <TableCell>{pov.variant.item.name}</TableCell>
                  <TableCell>{variantName(pov.variant) || "---"}</TableCell>
                  {usesItemCustomization && (
                    <TableCell>{pov.customization ?? "---"}</TableCell>
                  )}
                  <TableCell align="right">
                    {pov.variant.item.packSize}
                  </TableCell>
                  <TableCell align="right">{totalQty}</TableCell>
                  {!roleIs(permissions.supplier) && (
                    <TableCell align="right">
                      {formatMoneyString(pov.variant.item.cost)}
                    </TableCell>
                  )}
                  {hasDifferentCosts ? (
                    <TableCell
                      onClick={() => setTableView("itemized")}
                      tw="hover:underline cursor-pointer text-primary-600"
                      align="right"
                    >
                      See Itemized Costs
                    </TableCell>
                  ) : (
                    <TableCell align="right">
                      {formatMoneyString(pov.actualCost)}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {formatMoneyString(extendedTotal)}
                  </TableCell>
                </TableRow>
              );
            })}
            {setupFees.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={headCells.length} />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={2}>
                    <b>Set Up Fees:</b>
                  </TableCell>
                  <TableCell colSpan={headCells.length - 3} />
                  <TableCell align="right">
                    {formatMoneyString(
                      _.sumBy(setupFees, (pov) => +pov.actualCost)
                    )}
                  </TableCell>
                </TableRow>
              </>
            )}
            <PurchaseOrderVariantTableFooter
              totalRowsFirstCellLength={totalRowsFirstCellLength}
              overview={true}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PurchaseOrderVariantOverviewTable;
